import React from 'react';
import { ActivityIndicator, View } from 'react-native';

import Text from '../../../components/text/Text';
import Card from '../../../design/components/Card';
import PrimaryButton from '../../../design/components/PrimaryButton';
import StepRow from '../../../design/components/StepRow';
import { STEP_STATUS } from '../../../design/components/StatusIcon';
import { useTheme } from '../../../theme';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { getVehicleText } from '../../servicerun/ServiceRunDetail/ServiceRunVehicleDetail';
import { DRIVER_ROUTE } from '../driverRoutes';
import useEndOfDaySteps from '../endOfDay/useEndOfDaySteps';
import { getRequiredStepProgress } from '../stepStatus';

const PHASES = ['start', 'stops', 'end'];

const countLabel = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

const STATUS_TONE = {
	[STEP_STATUS.DONE]: 'success',
	[STEP_STATUS.CURRENT]: 'accent',
	[STEP_STATUS.TODO]: 'secondary',
};

// Where "continue" goes, by the phase the driver is working (day.activePhaseKey).
const CONTINUE_TARGET = {
	start: { route: DRIVER_ROUTE.START_OF_DAY, label: 'Continue start of day' },
	stops: { route: DRIVER_ROUTE.RUN_LIST, label: 'Continue on the run' },
	end: { route: DRIVER_ROUTE.END_OF_DAY, label: 'Continue end of day' },
};

/**
 * Today's run on Home: run name, machines and vehicle, the three phases with
 * progress, and one button to the right place.
 * Props:
 * - day: useDriverDay() result (serviceRun must be set)
 * - isMyDayEnabled: false for tenants without My day (no phases; opens the run list)
 * - isClockedIn, canUseClockStatus: "Start your day" before clock-in
 * - onOpen(routeName)
 */
const TodayRunCard = ({ day, isMyDayEnabled, isClockedIn, canUseClockStatus, onOpen }) => {
	const styles = useThemedStyles(createStyles);
	const {
		serviceRun,
		sortedDockets,
		activePhaseKey,
		visibleStartSteps,
		completedStopsCount,
		isTraining,
		trainingContext,
	} = day;
	// Same required-step counts as the Start of day and End of day screens.
	const { steps: endOfDaySteps } = useEndOfDaySteps(day);
	const startOfDayProgress = getRequiredStepProgress(visibleStartSteps);
	const endOfDayProgress = getRequiredStepProgress(endOfDaySteps);
	const machineCount = sortedDockets.length;
	const runDetails = [
		countLabel(machineCount, 'machine', 'machines'),
		serviceRun.vehicle?.id ? getVehicleText(serviceRun.vehicle) : null,
	].filter(Boolean).join(' · ');

	// A finished day (no next action) shows every phase done.
	const activeIndex = activePhaseKey ? PHASES.indexOf(activePhaseKey) : PHASES.length;
	const getStatus = index => (index < activeIndex
		? STEP_STATUS.DONE
		: index === activeIndex ? STEP_STATUS.CURRENT : STEP_STATUS.TODO);
	const phases = [
		{
			key: 'start',
			title: 'Start of day',
			current: `${startOfDayProgress.doneCount} of ${startOfDayProgress.totalCount}`,
			todo: countLabel(startOfDayProgress.totalCount, 'required step', 'required steps'),
		},
		{
			key: 'stops',
			title: 'On the run',
			current: `${completedStopsCount} of ${machineCount}`,
			todo: countLabel(machineCount, 'stop', 'stops'),
		},
		{
			key: 'end',
			title: 'End of day',
			current: `${endOfDayProgress.doneCount} of ${endOfDayProgress.totalCount}`,
			// Nothing to count yet (e.g. a training run before it starts).
			todo: endOfDayProgress.totalCount
				? countLabel(endOfDayProgress.totalCount, 'required step', 'required steps')
				: 'Starts after the run',
		},
	].map((phase, index) => {
		const status = getStatus(index);
		return {
			...phase,
			status,
			statusText: status === STEP_STATUS.DONE ? 'Done' : status === STEP_STATUS.CURRENT ? phase.current : phase.todo,
		};
	});

	let action;
	if (!isMyDayEnabled) {
		action = { route: DRIVER_ROUTE.RUN_LIST, label: 'Open run' };
	}
	else if (!activePhaseKey) {
		action = { route: DRIVER_ROUTE.END_OF_DAY, label: 'View end of day' };
	}
	else if (activePhaseKey === 'start' && canUseClockStatus && !isClockedIn && !isTraining) {
		action = { route: DRIVER_ROUTE.START_OF_DAY, label: 'Start your day' };
	}
	else {
		action = CONTINUE_TARGET[activePhaseKey];
	}

	return (
		<Card tone="current" style={styles.card}>
			<View style={styles.heading}>
				<Text style={styles.overline}>{isTraining ? `${trainingContext.targetDriverName}'s run` : 'Today\'s run'}</Text>
				<Text style={styles.title}>{serviceRun.route?.name || 'Routeless service run'}</Text>
				<Text style={styles.details}>{runDetails}</Text>
			</View>
			{
				isMyDayEnabled && (
					<View style={styles.phases}>
						{phases.map(phase => (
							<StepRow
								key={phase.key}
								variant="plain"
								status={phase.status}
								title={phase.title}
								statusText={phase.statusText}
								statusTone={STATUS_TONE[phase.status]}
							/>
						))}
					</View>
				)
			}
			{
				!isMyDayEnabled && (
					<Text style={styles.details}>{`${completedStopsCount} of ${machineCount} stops done`}</Text>
				)
			}
			<PrimaryButton label={action.label} onPress={() => onOpen(action.route)} />
		</Card>
	);
};

export const TodayRunLoadingCard = () => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();

	return (
		<Card style={styles.loadingCard} accessibilityLabel="Loading today's run">
			<ActivityIndicator color={colors.accent} />
		</Card>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	card: {
		gap: 14,
		borderRadius: radius.hero,
	},
	heading: {
		gap: space.xs,
	},
	overline: {
		...type.overline,
		color: colors.accent,
	},
	title: {
		...type.cardTitle,
		color: colors.text,
	},
	details: {
		...type.secondary,
		color: colors.currentSubtle,
	},
	phases: {
		gap: space.sm,
	},
	loadingCard: {
		minHeight: 120,
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: radius.hero,
	},
});

export default TodayRunCard;
